import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { Stack } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import API from "../APIs/API";
import "../styles/profile.scss";

const limitedFilter = createFilterOptions({ limit: 50 });

// Read-only plain input
const PlainField = ({ label, name, value }) => (
  <div className="form-outline" style={{ marginBottom: 12 }}>
    <label className="form-label" htmlFor={name}>
      <b>{label}</b>
    </label>
    <br />
    <input
      className="form-control"
      type="text"
      id={name}
      name={name}
      value={value ?? ""}
      disabled
    />
  </div>
);

// FreeSolo Autocomplete with DB-sourced suggestions
const DropdownField = ({ label, name, value, onChange, options = [] }) => (
  <div className="form-outline" style={{ marginBottom: 12 }}>
    <label className="form-label" htmlFor={name}>
      <b>{label}</b>
    </label>
    <Autocomplete
      freeSolo
      filterOptions={limitedFilter}
      options={options}
      value={value ?? ""}
      onChange={(_, newValue) =>
        onChange({ target: { name, value: newValue ?? "" } })
      }
      onInputChange={(_, newValue, reason) => {
        if (reason === "input")
          onChange({ target: { name, value: newValue ?? "" } });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          variant="outlined"
          name={name}
          id={name}
          sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff", fontSize: 14 } }}
        />
      )}
      sx={{ mt: 0.5 }}
    />
  </div>
);

const initialForm = {
  position_no: "",
  occup_pos_title: "",
  award: "",
  award_desc: "",
  classification: "",
  class_desc: "",
  occup_type: "",
  account_no: "",
  account_no_desc: "",
  paypoint: "",
  paypoint_desc: "",
  occup_pos_cat: "",
};

const StaffAdd = ({ type }) => {
  const navigate = useNavigate();
  const { admin_location_id } = useParams();
  const pageUserType = type === "Non-Teacher" ? "non-teachers" : "teachers";
  const backUrl = `/${pageUserType}/${admin_location_id}/staff`;

  const [form, setForm] = useState(initialForm);
  const [adminLocation, setAdminLocation] = useState(null);

  // Fetch admin location details for the read-only header fields
  useEffect(() => {
    new API().getAdminLocationById(admin_location_id).then((result) => {
      if (result?.data) setAdminLocation(result.data);
    });
  }, [admin_location_id]);

  // Fetch field options (lazy: always fetch here since user is in "add" mode)
  const { data: fieldOptions = {} } = useQuery(["staff_field_options"], {
    queryFn: async () => {
      const result = await new API().getStaffFieldOptions();
      return result?.data ?? {};
    },
    staleTime: 5 * 60 * 1000,
  });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const createMutation = useMutation(
    (body) => new API().createStaffEstablishment(body),
    {
      onSuccess: (data) => {
        if (data?.status === "ok") {
          toast.success("Position added successfully!");
          navigate(backUrl);
        } else {
          toast.error("Failed to add position. Please try again.");
        }
      },
      onError: () => toast.error("Failed to add position."),
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate({
      admin_location_id: Number(admin_location_id),
      ...form,
    });
  };

  const opts = fieldOptions;

  return (
    <div className="wrapper">
      <div className="sidebar">
        <Sidebar />
      </div>
      <div className="content">
        <div className="profile">
          <div className="profileContainer">
            <div className="profileSection">
              <Link to={backUrl} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br />
              <br />

              <form onSubmit={handleSubmit}>
                <div className="container">
                  <div className="row">
                    <div className="col col-sm">
                      <h1>Add New Position</h1>
                    </div>
                  </div>
                  <hr />

                  <div className="row">
                    {/* ── Column 1 ── */}
                    <div className="col col-sm">
                      <PlainField label="Admin Location" name="admin_location" value={adminLocation?.admin_location ?? ""} />
                      <PlainField label="Admin Desc"     name="admin_desc"     value={adminLocation?.admin_desc ?? ""} />
                      <DropdownField label="Position No"    name="position_no"    value={form.position_no}    onChange={handleChange} options={opts.position_no ?? []} />
                      <DropdownField label="Occup Pos Title" name="occup_pos_title" value={form.occup_pos_title} onChange={handleChange} options={opts.occup_pos_title ?? []} />
                      <DropdownField label="Award"           name="award"           value={form.award}           onChange={handleChange} options={opts.award ?? []} />
                      <DropdownField label="Award Desc"      name="award_desc"      value={form.award_desc}      onChange={handleChange} options={opts.award_desc ?? []} />
                    </div>

                    {/* ── Column 2 ── */}
                    <div className="col col-sm">
                      <DropdownField label="Classification"  name="classification"  value={form.classification}  onChange={handleChange} options={opts.classification ?? []} />
                      <DropdownField label="Class Desc"      name="class_desc"      value={form.class_desc}      onChange={handleChange} options={opts.class_desc ?? []} />
                      <DropdownField label="Occup Type"      name="occup_type"      value={form.occup_type}      onChange={handleChange} options={opts.occup_type ?? []} />
                      <DropdownField label="Account No"      name="account_no"      value={form.account_no}      onChange={handleChange} options={opts.account_no ?? []} />
                      <DropdownField label="Account No Desc" name="account_no_desc" value={form.account_no_desc} onChange={handleChange} options={opts.account_no_desc ?? []} />
                      <DropdownField label="Paypoint"        name="paypoint"        value={form.paypoint}        onChange={handleChange} options={opts.paypoint ?? []} />
                      <DropdownField label="Paypoint Desc"   name="paypoint_desc"   value={form.paypoint_desc}   onChange={handleChange} options={opts.paypoint_desc ?? []} />
                      <DropdownField label="Occup Pos Cat"   name="occup_pos_cat"   value={form.occup_pos_cat}   onChange={handleChange} options={opts.occup_pos_cat ?? []} />
                    </div>
                  </div>

                  <hr />
                  <Stack direction="row" spacing={1}>
                    <Link
                      className="app-button app-button-gray"
                      to={backUrl}
                      style={{ marginRight: 10 }}
                    >
                      Back
                    </Link>
                    <button
                      type="submit"
                      className="app-button app-button-primary"
                      disabled={createMutation.isLoading}
                    >
                      {createMutation.isLoading ? (
                        <>
                          <CircularProgress size={14} color="inherit" style={{ marginRight: 6 }} />
                          Creating…
                        </>
                      ) : (
                        "Create"
                      )}
                    </button>
                  </Stack>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffAdd;
